import { useState, useCallback, useRef, useEffect } from 'react';
import { AudioChunk, AudioCaptureConfig, AudioFormat } from '../types/audio';
import { AudioProcessor } from '../services/AudioProcessor';
import { AudioChunker, ChunkingConfig } from '../services/AudioChunker';
import { BrowserCompatibility, PermissionErrorHandler } from '../services/BrowserCompatibility';

export interface AudioCaptureState {
  /** Whether microphone permission has been granted */
  hasPermission: boolean;
  /** Whether permission request is in progress */
  isRequestingPermission: boolean;
  /** Whether audio capture is currently active */
  isCapturing: boolean;
  /** Whether Web Audio API is supported */
  isSupported: boolean;
  /** Current error message, if any */
  error: string | null;
  /** Current audio format configuration */
  audioFormat: AudioFormat | null;
}

export interface AudioCaptureActions {
  /** Request microphone permission */
  requestPermission: () => Promise<boolean>;
  /** Start audio capture */
  startCapture: (config: AudioCaptureConfig, deviceId?: string, forceStart?: boolean) => Promise<boolean>;
  /** Stop audio capture */
  stopCapture: () => void;
  /** Clear current error */
  clearError: () => void;
}

export interface UseAudioCaptureReturn {
  state: AudioCaptureState;
  actions: AudioCaptureActions;
}

/**
 * Hook for managing microphone permission and audio capture
 * Handles Web Audio API integration and browser compatibility
 */
export const useAudioCapture = (
  onAudioChunk?: (chunk: AudioChunk) => void
): UseAudioCaptureReturn => {
  const [state, setState] = useState<AudioCaptureState>({
    hasPermission: false,
    isRequestingPermission: false,
    isCapturing: false,
    isSupported: typeof navigator !== 'undefined' && 
                 !!navigator.mediaDevices && 
                 !!navigator.mediaDevices.getUserMedia &&
                 typeof AudioContext !== 'undefined',
    error: null,
    audioFormat: null,
  });

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioProcessorRef = useRef<AudioProcessor | null>(null);
  const audioChunkerRef = useRef<AudioChunker | null>(null);

  // Check for permission revocation
  useEffect(() => {
    if (!state.isSupported) return;

    const checkPermission = async () => {
      try {
        const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        const handlePermissionChange = () => {
          if (permission.state === 'denied' && state.hasPermission) {
            setState((prev: AudioCaptureState) => ({
              ...prev,
              hasPermission: false,
              error: 'Microphone permission was revoked. Please grant permission to continue.',
            }));
            stopCapture();
          }
        };

        permission.addEventListener('change', handlePermissionChange);
        
        return () => {
          permission.removeEventListener('change', handlePermissionChange);
        };
      } catch (error) {
        // Permission API not supported, ignore
      }
    };

    checkPermission();
  }, [state.hasPermission, state.isSupported]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    console.log('🔐 [useAudioCapture] Requesting microphone permission...');
    
    // Check browser compatibility first
    const compatibilityReport = BrowserCompatibility.generateCompatibilityReport();
    console.log('🔍 [useAudioCapture] Compatibility report:', compatibilityReport);
    
    if (!compatibilityReport.overallSupported) {
      console.error('❌ [useAudioCapture] Browser not supported:', compatibilityReport.criticalIssues);
      setState((prev: AudioCaptureState) => ({
        ...prev,
        error: compatibilityReport.criticalIssues.join('. '),
        isSupported: false,
      }));
      return false;
    }

    setState((prev: AudioCaptureState) => ({ ...prev, isRequestingPermission: true, error: null }));

    try {
      console.log('🔐 [useAudioCapture] Calling getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log('✅ [useAudioCapture] getUserMedia successful, stream:', stream);
      console.log('🎵 [useAudioCapture] Audio tracks:', stream.getAudioTracks());

      // Test successful - stop the stream immediately
      stream.getTracks().forEach((track: MediaStreamTrack) => {
        console.log('🛑 [useAudioCapture] Stopping track:', track.label);
        track.stop();
      });

      setState((prev: AudioCaptureState) => ({
        ...prev,
        hasPermission: true,
        isRequestingPermission: false,
        error: null,
      }));

      return true;
    } catch (error) {
      const errorInfo = PermissionErrorHandler.getPermissionErrorMessage(error as Error);
      
      setState((prev: AudioCaptureState) => ({
        ...prev,
        hasPermission: false,
        isRequestingPermission: false,
        error: `${errorInfo.title}: ${errorInfo.message}`,
      }));

      return false;
    }
  }, []);

  const startCapture = useCallback(async (config: AudioCaptureConfig, deviceId?: string, forceStart: boolean = false): Promise<boolean> => {
    console.log('▶️ [useAudioCapture] Starting audio capture with config:', config);
    console.log('🔐 [useAudioCapture] Has permission:', state.hasPermission);
    console.log('🎵 [useAudioCapture] Is capturing:', state.isCapturing);
    console.log('🔧 [useAudioCapture] Force start:', forceStart);
    
    if (!state.hasPermission && !forceStart) {
      console.error('❌ [useAudioCapture] No microphone permission');
      setState((prev: AudioCaptureState) => ({
        ...prev,
        error: 'Microphone permission not granted. Please request permission first.',
      }));
      return false;
    }

    if (state.isCapturing) {
      console.log('⚠️ [useAudioCapture] Already capturing, returning true');
      return true; // Already capturing
    }

    try {
      // Validate audio format
      console.log('🔍 [useAudioCapture] Validating audio format...');
      const formatValidation = AudioProcessor.validateFormat(config.format);
      console.log('🔍 [useAudioCapture] Format validation result:', formatValidation);
      
      if (!formatValidation.valid) {
        console.error('❌ [useAudioCapture] Invalid audio format:', formatValidation.error);
        setState((prev: AudioCaptureState) => ({
          ...prev,
          error: formatValidation.error || 'Invalid audio format',
        }));
        return false;
      }

      // Request media stream
      console.log('🎵 [useAudioCapture] Requesting media stream...');
      console.log('🎯 [useAudioCapture] Using device ID:', deviceId || 'default');
      
      const audioConstraints: MediaTrackConstraints = {
        sampleRate: config.format.sampleRate,
        channelCount: config.format.channels,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };

      // Add device ID constraint if specified
      if (deviceId && deviceId !== 'default') {
        audioConstraints.deviceId = { exact: deviceId };
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      console.log('✅ [useAudioCapture] Media stream obtained:', stream);
      console.log('🎵 [useAudioCapture] Stream tracks:', stream.getTracks());
      mediaStreamRef.current = stream;

      // Create audio processor
      console.log('🔧 [useAudioCapture] Creating audio processor...');
      const audioProcessor = new AudioProcessor();
      audioProcessorRef.current = audioProcessor;

      // Create audio chunker
      console.log('🔧 [useAudioCapture] Creating audio chunker...');
      const chunkingConfig: ChunkingConfig = AudioChunker.createOptimalConfig(config.format, config.chunkSizeMs);
      console.log('🔧 [useAudioCapture] Chunking config:', chunkingConfig);
      const audioChunker = new AudioChunker(chunkingConfig, config.format, onAudioChunk);
      audioChunkerRef.current = audioChunker;

      // Initialize audio processor
      console.log('🔧 [useAudioCapture] Initializing audio processor...');
      const initialized = await audioProcessor.initialize(config.format, (chunk) => {
        audioChunker.addAudioData(chunk.data);
      });

      if (!initialized) {
        throw new Error('Failed to initialize audio processor');
      }

      // Start processing
      const processingStarted = audioProcessor.startProcessing(stream, config.format);
      if (!processingStarted) {
        throw new Error('Failed to start audio processing');
      }

      setState((prev: AudioCaptureState) => ({
        ...prev,
        isCapturing: true,
        error: null,
        audioFormat: config.format,
      }));

      return true;
    } catch (error) {
      const errorInfo = PermissionErrorHandler.getPermissionErrorMessage(error as Error);
      
      setState((prev: AudioCaptureState) => ({
        ...prev,
        error: `${errorInfo.title}: ${errorInfo.message}`,
      }));

      return false;
    }
  }, [state.hasPermission, state.isCapturing, onAudioChunk]);

  const stopCapture = useCallback(() => {
    // Flush any remaining audio data
    if (audioChunkerRef.current) {
      audioChunkerRef.current.flush();
      audioChunkerRef.current = null;
    }

    // Stop audio processor
    if (audioProcessorRef.current) {
      audioProcessorRef.current.stopProcessing();
      audioProcessorRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      mediaStreamRef.current = null;
    }

    setState((prev: AudioCaptureState) => ({
      ...prev,
      isCapturing: false,
      audioFormat: null,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev: AudioCaptureState) => ({ ...prev, error: null }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCapture();
    };
  }, [stopCapture]);

  return {
    state,
    actions: {
      requestPermission,
      startCapture,
      stopCapture,
      clearError,
    },
  };
};